import type {
  EncodingPlan,
  EncodingStream,
  PivotMedia,
  SourceMedia,
} from "../api/EncoderAPI.types";

// ---------------------------------------------------------------------------
// Quota computation types
// ---------------------------------------------------------------------------

export interface QuotaBucket {
  seconds: number;
  percent: number;
}

export interface ComputedQuotas {
  current: QuotaBucket;
  used: QuotaBucket;
  available: QuotaBucket;
  usedAfterEncoding: QuotaBucket;
  availableAfterEncoding: QuotaBucket;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function roundPercent(value: number): number {
  return Math.round(value * 10) / 10;
}

function getVideoQuality(height: number): string {
  if (height >= 2160) return "2160";
  if (height >= 1080) return "1080";
  if (height >= 720) return "720";
  if (height >= 576) return "576";
  if (height >= 480) return "480";
  return "360";
}

/**
 * Converts source media video streams into synthetic EncodingStream objects
 * suitable for quota estimation. Only video streams are included because
 * audio streams contribute negligibly and are not present in SourceMedia.
 */
export function sourceMediasToEncodingStreams(
  sourceMedias: (SourceMedia | PivotMedia)[],
): Pick<EncodingStream, "type" | "duration" | "uuid">[] {
  return sourceMedias.flatMap((source) =>
    (source.info?.streams ?? [])
      .filter((s) => s.codec_type === "video")
      .map((s) => ({
        type: "video" as const,
        quality: getVideoQuality(s.height ?? 0),
        duration: s.duration ? parseFloat(s.duration) : 0,
        uuid: "", // empty uuid means it counts toward quotaCurrent
      })),
  );
}

function getCurrentQuota(
  encodingStreams: Pick<EncodingStream, "type" | "duration" | "uuid">[],
  costFactor: number,
): number {
  return encodingStreams.reduce((total, stream) => {
    if ((stream.type === "video" || stream.type === "audio") && !stream.uuid) {
      return total + (stream.duration ?? 0) * costFactor;
    }
    return total;
  }, 0);
}

export function getComputedQuotas(
  encodingStreams: Pick<EncodingStream, "type" | "duration" | "uuid">[],
  encodingPlan: EncodingPlan | null | undefined,
  costFactor: number,
): ComputedQuotas {
  const quotaUsed = encodingPlan?.encoding_sec ?? 0;
  const quotaFull = encodingPlan?.max_encoding_sec ?? 0;

  const quotaCurrent = getCurrentQuota(encodingStreams, costFactor);
  const quotaAvailable = quotaFull - quotaUsed;
  const quotaAfterEncoding = quotaUsed + quotaCurrent;
  const quotaAvailableAfterEncoding = quotaFull - quotaAfterEncoding;

  return {
    current: {
      seconds: quotaCurrent,
      percent:
        quotaFull > 0 ? roundPercent((quotaCurrent / quotaFull) * 100) : 0,
    },
    used: {
      seconds: quotaUsed,
      percent:
        quotaFull > 0
          ? roundPercent((quotaAfterEncoding / quotaFull) * 100)
          : 0,
    },
    available: {
      seconds: quotaAvailable,
      percent:
        quotaFull > 0
          ? roundPercent(((quotaFull - quotaUsed) / quotaFull) * 100)
          : 0,
    },
    usedAfterEncoding: {
      seconds: quotaAfterEncoding,
      percent:
        quotaFull > 0
          ? roundPercent((quotaAfterEncoding / quotaFull) * 100)
          : 0,
    },
    availableAfterEncoding: {
      seconds: quotaAvailableAfterEncoding,
      percent:
        quotaFull > 0
          ? roundPercent((quotaAvailableAfterEncoding / quotaFull) * 100)
          : 0,
    },
  };
}
